/**
 * Note: This form is using card from Stripe Elements https://stripe.com/docs/stripe-js#elements
 * Card is not a Final Form field so it's not available trough Final Form.
 * It's also handled separately in handleSubmit function.
 */
import React, { Component } from 'react';
import { bool, func, node, object, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import config from '../../config';
import { propTypes } from '../../util/types';
import { ensurePaymentMethodCard } from '../../util/data';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';

import {
  Form,
  PrimaryButton,
  FieldCheckbox,
  FieldSelect,
  FieldTextInput,
  IconSpinner,
  SavedCardDetails,
  StripePaymentAddress,
} from '../../components';
import css from './StripePaymentForm.css';

/**
 * Translate a Stripe API error object.
 *
 * To keep up with possible keys from the Stripe API, see:
 *
 * https://stripe.com/docs/api#errors
 *
 * Note that at least at moment, the above link doesn't list all the
 * error codes that the API returns.
 *
 * @param {Object} intl - react-intl object from injectIntl
 * @param {Object} stripeError - error object from Stripe API
 *
 * @return {String} translation message for the specific Stripe error,
 * or the given error message (not translated) if the specific error
 * type/code is not defined in the translations
 *
 */
const stripeErrorTranslation = (intl, stripeError) => {
  const { message, code, type } = stripeError;

  if (!code || !type) {
    // Not a proper Stripe error object
    return intl.formatMessage({ id: 'StripePaymentForm.genericError' });
  }

  const translationId =
    type === 'validation_error'
      ? `StripePaymentForm.stripe.validation_error.${code}`
      : `StripePaymentForm.stripe.${type}`;

  return intl.formatMessage({
    id: translationId,
    defaultMessage: message,
  });
};

const stripeElementsOptions = {
  fonts: [
    {
      family: 'sofiapro',
      fontSmoothing: 'antialiased',
      src:
        'local("sofiapro"), local("SofiaPro"), local("Sofia Pro"), url("https://assets-sharetribecom.sharetribe.com/webfonts/sofiapro/sofiapro-medium-webfont.woff2") format("woff2")',
    },
  ],
};

const cardStyles = {
  base: {
    fontFamily: '"sofiapro", Helvetica, Arial, sans-serif',
    fontSize: '15px',
    fontSmoothing: 'antialiased',
    lineHeight: '24px',
    letterSpacing: '-0.1px',
    color: '#111827',
    '::placeholder': {
      color: '#9ca3af',
    },
  },
};

const OneTimePaymentWithCardElement = props => {
  const { cardClasses, formId, handleStripeElementRef, hasCardError, error, label, intl } = props;
  const labelText =
    label || intl.formatMessage({ id: 'StripePaymentForm.saveAfterOnetimePayment' });
  return (
    <React.Fragment>
      <label className={css.paymentLabel} htmlFor={`${formId}-card`}>
        <FormattedMessage id="StripePaymentForm.paymentCardDetails" />
      </label>
      <div className={cardClasses} id={`${formId}-card`} ref={handleStripeElementRef} />
      {hasCardError ? <span className={css.error}>{error}</span> : null}
      <div className={css.saveForLaterUse}>
        <FieldCheckbox
          className={css.saveForLaterUseCheckbox}
          textClassName={css.saveForLaterUseLabel}
          id="saveAfterOnetimePayment"
          name="saveAfterOnetimePayment"
          label={labelText}
          value="saveAfterOnetimePayment"
          useSuccessColor
        />
        <span className={css.saveForLaterUseLegalInfo}>
          <FormattedMessage id="StripePaymentForm.saveforLaterUseLegalInfo" />
        </span>
      </div>
    </React.Fragment>
  );
};

const getPaymentMethod = (selectedPaymentMethod, hasDefaultPaymentMethod) => {
  return selectedPaymentMethod == null && hasDefaultPaymentMethod
    ? 'defaultCard'
    : selectedPaymentMethod == null
    ? 'onetimeCardPayment'
    : selectedPaymentMethod;
};

const initialState = {
  error: null,
  cardValueValid: false,
  // The mode can be 'onetimePayment', 'defaultCard', or 'replaceCard'
  // Check SavedCardDetails component for more information
  paymentMethod: null,
};

/**
 * Payment form that asks for credit card info using Stripe Elements.
 *
 * When the card is valid and the user submits the form, a request is
 * sent to the Stripe API to handle payment. `stripe.handleCardPayment`
 * may ask more details from cardholder if 3D security steps are needed.
 *
 * See: https://stripe.com/docs/payments/payment-intents
 *      https://stripe.com/docs/elements
 */
class StripePaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.handleCardValueChange = this.handleCardValueChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.paymentForm = this.paymentForm.bind(this);
    this.initializeStripeElement = this.initializeStripeElement.bind(this);
    this.handleStripeElementRef = this.handleStripeElementRef.bind(this);
    this.changePaymentMethod = this.changePaymentMethod.bind(this);
    this.handleSubmitButtonClick = this.handleSubmitButtonClick.bind(this);
    this.finalFormAPI = null;
    this.cardContainer = null;
  }

  componentDidMount() {
    if (!window.Stripe) {
      throw new Error('Stripe must be loaded for StripePaymentForm');
    }

    if (config.stripe.publishableKey) {
      const {
        onStripeInitialized,
        hasHandledCardPayment,
        defaultPaymentMethod,
        loadingData,
      } = this.props;
      this.stripe = window.Stripe(config.stripe.publishableKey);
      onStripeInitialized(this.stripe);

      if (!(hasHandledCardPayment || defaultPaymentMethod || loadingData)) {
        this.initializeStripeElement();
      }
    }
  }

  componentWillUnmount() {
    if (this.card) {
      this.card.removeEventListener('change', this.handleCardValueChange);
      this.card.unmount();
      this.card = null;
    }
  }

  initializeStripeElement(element) {
    const elements = this.stripe.elements(stripeElementsOptions);

    if (!this.card) {
      this.card = elements.create('card', { style: cardStyles });
      this.card.mount(element || this.cardContainer);
      this.card.addEventListener('change', this.handleCardValueChange);
      // EventListener is the only way to simulate breakpoints with Stripe.
      window.addEventListener('resize', () => {
        if (this.card) {
          if (window.innerWidth < 1024) {
            this.card.update({ style: { base: { fontSize: '15px', lineHeight: '24px' } } });
          } else {
            this.card.update({ style: { base: { fontSize: '16px', lineHeight: '26px' } } });
          }
        }
      });
    }
  }

  changePaymentMethod(changedTo) {
    if (this.card && changedTo === 'defaultCard') {
      this.card.removeEventListener('change', this.handleCardValueChange);
      this.card.unmount();
      this.card = null;
    }
    this.setState({ paymentMethod: changedTo });
  }

  // Runs on every click of the submit button, even when the form can't
  // actually be submitted yet. If a billing field is missing, touch every
  // field (so each one's own inline error becomes visible) and scroll to
  // the first invalid one. If billing is fine but the card isn't, scroll to
  // and focus the Stripe card field instead. Real submission is still
  // safely gated in handleSubmit()/react-final-form, this is only about
  // showing the user what's missing.
  handleSubmitButtonClick() {
    const formApi = this.finalFormAPI;
    if (!formApi) {
      return;
    }
    const { invalid, errors } = formApi.getState();

    if (invalid) {
      const fieldNames = formApi.getRegisteredFields();
      fieldNames.forEach(name => {
        formApi.focus(name);
        formApi.blur(name);
      });
      const firstErrorField = fieldNames.find(name => errors[name]);
      const el = firstErrorField
        ? document.querySelector(`[name="${firstErrorField}"]`)
        : null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }

    const { hasHandledCardPayment, defaultPaymentMethod } = this.props;
    const billingDetailsKnown = hasHandledCardPayment || defaultPaymentMethod;
    const onetimePaymentNeedsAttention = !billingDetailsKnown && !this.state.cardValueValid;
    if (onetimePaymentNeedsAttention && this.card) {
      if (this.cardContainer) {
        this.cardContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      this.card.focus();
    }
  }

  handleStripeElementRef(el) {
    this.cardContainer = el;
    if (this.stripe && el) {
      this.initializeStripeElement(el);
    }
  }

  handleCardValueChange(event) {
    const { intl } = this.props;
    const { error, complete } = event;

    this.setState({
      error: error ? stripeErrorTranslation(intl, error) : null,
      cardValueValid: complete,
    });
  }

  handleSubmit(values) {
    const {
      onSubmit,
      inProgress,
      formId,
      hasHandledCardPayment,
      defaultPaymentMethod,
    } = this.props;
    const { initialMessage } = values;
    const { cardValueValid, paymentMethod } = this.state;
    const billingDetailsKnown = hasHandledCardPayment || defaultPaymentMethod;
    const onetimePaymentNeedsAttention = !billingDetailsKnown && !cardValueValid;

    if (inProgress || onetimePaymentNeedsAttention) {
      // Already submitting or card value incomplete/invalid
      return;
    }

    const params = {
      message: initialMessage ? initialMessage.trim() : null,
      card: this.card,
      formId,
      formValues: values,
      paymentMethod: getPaymentMethod(
        paymentMethod,
        ensurePaymentMethodCard(defaultPaymentMethod).id
      ),
    };
    onSubmit(params);
  }

  paymentForm(formRenderProps) {
    const {
      className,
      rootClassName,
      inProgress: submitInProgress,
      loadingData,
      formId,
      authorDisplayName,
      showInitialMessageInput,
      intl,
      initiateOrderError,
      handleCardPaymentError,
      confirmPaymentError,
      invalid,
      handleSubmit,
      form,
      hasHandledCardPayment,
      defaultPaymentMethod,
      listingInfoCard,
      bookingSummaryCard,
      trustBullets,
    } = formRenderProps;

    this.finalFormAPI = form;

    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(defaultPaymentMethod);
    const billingDetailsNeeded = !(hasHandledCardPayment || confirmPaymentError);
    const billingDetailsKnown = hasHandledCardPayment || ensuredDefaultPaymentMethod;
    const onetimePaymentNeedsAttention = !billingDetailsKnown && !this.state.cardValueValid;
    // Native `disabled` only blocks while actually submitting — an invalid
    // form or incomplete card still needs to be clickable so
    // handleSubmitButtonClick can scroll/focus the user to what's missing.
    // handleSubmit() itself still refuses to call onSubmit in either case.
    const submitBlocked = invalid || onetimePaymentNeedsAttention;
    const submitDisabled = submitInProgress;
    const hasCardError = this.state.error && !submitInProgress;
    const hasPaymentErrors = handleCardPaymentError || confirmPaymentError;
    const classes = classNames(rootClassName || css.root, className);
    const cardClasses = classNames(css.card, {
      [css.cardSuccess]: this.state.cardValueValid,
      [css.cardError]: hasCardError,
    });

    // TODO: handleCardPayment can create all kinds of errors.
    // Currently, we provide translation support for one:
    // https://stripe.com/docs/error-codes
    const piAuthenticationFailure = 'payment_intent_authentication_failure';
    const paymentErrorMessage =
      handleCardPaymentError && handleCardPaymentError.code === piAuthenticationFailure
        ? intl.formatMessage({ id: 'StripePaymentForm.handleCardPaymentError' })
        : handleCardPaymentError
        ? handleCardPaymentError.message
        : confirmPaymentError
        ? intl.formatMessage({ id: 'StripePaymentForm.confirmPaymentError' })
        : intl.formatMessage({ id: 'StripePaymentForm.genericError' });

    const billingDetailsNameLabel = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNameLabel',
    });

    const billingDetailsNamePlaceholder = intl.formatMessage({
      id: 'StripePaymentForm.billingDetailsNamePlaceholder',
    });

    const messagePlaceholder = intl.formatMessage(
      { id: 'StripePaymentForm.messagePlaceholder' },
      { name: authorDisplayName }
    );

    const messageOptionalText = intl.formatMessage({
      id: 'StripePaymentForm.messageOptionalText',
    });

    const initialMessageLabel = intl.formatMessage(
      { id: 'StripePaymentForm.messageLabel' },
      { messageOptionalText: messageOptionalText }
    );

    const countryLabel = intl.formatMessage({ id: 'StripePaymentAddress.countryLabel' });
    const countryPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.countryPlaceholder' });
    const countryRequired = validators.required(
      intl.formatMessage({ id: 'StripePaymentAddress.countryRequired' })
    );
    const countryCodes = getCountryCodes(config.locale);

    // Asking billing address is recommended in PaymentIntent flow.
    // In CheckoutPage, we send name and email as billing details, but address only if it exists.
    const billingAddress = (
      <StripePaymentAddress intl={intl} form={form} fieldId={formId} />
    );

    const hasStripeKey = config.stripe.publishableKey;
    const showPaymentMethodSelector = ensuredDefaultPaymentMethod.id;
    const selectedPaymentMethod = getPaymentMethod(
      this.state.paymentMethod,
      showPaymentMethodSelector
    );
    const showOnetimePaymentFields = ['onetimeCardPayment', 'replaceCard'].includes(
      selectedPaymentMethod
    );

    const cardField = (label = null) => (
      <OneTimePaymentWithCardElement
        cardClasses={cardClasses}
        formId={formId}
        handleStripeElementRef={this.handleStripeElementRef}
        hasCardError={hasCardError}
        error={this.state.error}
        label={label}
        intl={intl}
      />
    );

    return hasStripeKey ? (
      <Form className={classes} onSubmit={handleSubmit}>
        <div className={css.leftColInner}>
          {listingInfoCard}

          <div className={css.sectionCard}>
            <div className={css.sectionHeader}>
              <span className={css.sectionNum}>02</span>
              <h3 className={css.sectionTitle}>Billing address</h3>
              <span className={css.sectionHint}>Used for your receipt</span>
            </div>

            {billingDetailsNeeded && !loadingData ? (
              showOnetimePaymentFields ? (
                <React.Fragment>
                  <FieldSelect
                    id={`${formId}.country`}
                    name="country"
                    className={css.countryField}
                    label={countryLabel}
                    validate={countryRequired}
                  >
                    <option disabled value="">
                      {countryPlaceholder}
                    </option>
                    {countryCodes.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </FieldSelect>

                  <FieldTextInput
                    className={css.nameField}
                    inputRootClass={css.inputBox}
                    type="text"
                    id="name"
                    name="name"
                    autoComplete="cc-name"
                    label={billingDetailsNameLabel}
                    placeholder={billingDetailsNamePlaceholder}
                    validate={validators.required('Full name is required')}
                  />

                  {billingAddress}
                </React.Fragment>
              ) : (
                <p className={css.sectionSubtext}>Using your saved billing details.</p>
              )
            ) : loadingData ? (
              <p className={css.spinner}>
                <IconSpinner />
              </p>
            ) : null}

            {showInitialMessageInput ? (
              <div className={css.messageBlock}>
                <h3 className={css.sectionSubheading}>
                  <FormattedMessage id="StripePaymentForm.messageHeading" />
                </h3>

                <FieldTextInput
                  type="textarea"
                  id={`${formId}-message`}
                  name="initialMessage"
                  label={initialMessageLabel}
                  placeholder={messagePlaceholder}
                  className={css.message}
                  inputRootClass={css.inputBox}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className={css.rightColInner}>
          {bookingSummaryCard}

          <div className={css.sectionCard}>
            <div className={css.sectionHeader}>
              <span className={css.sectionNum}>03</span>
              <h3 className={css.sectionTitle}>Payment method</h3>
            </div>
            <p className={css.sectionSubtext}>Encrypted and processed by Stripe.</p>

            {billingDetailsNeeded && !loadingData ? (
              showPaymentMethodSelector ? (
                <React.Fragment>
                  <SavedCardDetails
                    className={css.paymentMethodSelector}
                    card={ensuredDefaultPaymentMethod.attributes.card}
                    onChange={this.changePaymentMethod}
                  />
                  {selectedPaymentMethod === 'replaceCard'
                    ? cardField(
                        intl.formatMessage(
                          { id: 'StripePaymentForm.replaceAfterOnetimePayment' },
                          { last4Digits: ensuredDefaultPaymentMethod.attributes.card.last4Digits }
                        )
                      )
                    : null}
                </React.Fragment>
              ) : (
                cardField()
              )
            ) : loadingData ? (
              <p className={css.spinner}>
                <IconSpinner />
              </p>
            ) : null}

            {initiateOrderError ? (
              <span className={css.errorMessage}>{initiateOrderError.message}</span>
            ) : null}
            {hasPaymentErrors ? (
              <span className={css.errorMessage}>{paymentErrorMessage}</span>
            ) : null}

            <div className={css.totalDueRow}>
              <span className={css.totalDueLabel}>Total due if accepted</span>
              <span className={css.totalDueValue}>{this.props.totalDueFormatted}</span>
            </div>

            <PrimaryButton
              className={classNames(css.submitButton, { [css.submitButtonBlocked]: submitBlocked })}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              onClick={this.handleSubmitButtonClick}
            >
              <FormattedMessage id="StripePaymentForm.submitPaymentInfo" />
            </PrimaryButton>
            {submitBlocked && !submitInProgress ? (
              <p className={css.helperText}>
                {invalid
                  ? 'Complete your billing address to send this request.'
                  : 'Enter your card details to send this request.'}
              </p>
            ) : null}
          </div>

          {trustBullets}
        </div>
      </Form>
    ) : (
      <div className={css.missingStripeKey}>
        <FormattedMessage id="StripePaymentForm.missingStripeKey" />
      </div>
    );
  }

  render() {
    const { onSubmit, ...rest } = this.props;
    return <FinalForm onSubmit={this.handleSubmit} {...rest} render={this.paymentForm} />;
  }
}

StripePaymentForm.defaultProps = {
  className: null,
  rootClassName: null,
  inProgress: false,
  loadingData: false,
  showInitialMessageInput: true,
  hasHandledCardPayment: false,
  defaultPaymentMethod: null,
  initiateOrderError: null,
  handleCardPaymentError: null,
  confirmPaymentError: null,
  listingInfoCard: null,
  bookingSummaryCard: null,
  trustBullets: null,
  totalDueFormatted: null,
};

StripePaymentForm.propTypes = {
  className: string,
  rootClassName: string,
  inProgress: bool,
  loadingData: bool,
  initiateOrderError: object,
  handleCardPaymentError: object,
  confirmPaymentError: object,
  formId: string.isRequired,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  authorDisplayName: string.isRequired,
  showInitialMessageInput: bool,
  hasHandledCardPayment: bool,
  defaultPaymentMethod: propTypes.defaultPaymentMethod,
  listingInfoCard: node,
  bookingSummaryCard: node,
  trustBullets: node,
  totalDueFormatted: string,
};

export default injectIntl(StripePaymentForm);
